
import React, { useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, ExternalLink, Brain, Palette, Settings, Users } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SectionHeader from '../components/ui/section-header';
import PageSection from '../components/ui/page-section';

const Portfolio: React.FC = () => {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Real project data
  const projects = [
    {
      id: 1,
      title: "PersonaAI",
      icon: <Brain className="h-12 w-12 text-primary" />,
      image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=500&q=80",
      description: "Behavioral AI platform that conducts automated focus groups using psychologically-grounded personas. Generates market research insights through simulated conversations and behavioral modeling.",
      capabilities: "Advanced AI conversation engines, psychological trait architecture, behavioral prediction systems, research automation",
      whatThisMeans: "We can build AI that understands and predicts human behavior for any industry or use case",
      technologies: ["Natural Language Processing", "Behavioral Modeling", "Psychological AI", "Research Automation"]
    },
    {
      id: 2,
      title: "CharX World Builder",
      icon: <Palette className="h-12 w-12 text-primary" />,
      image: "https://images.unsplash.com/photo-1550745165-9bc0b252726f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=500&q=80",
      description: "AI-powered character creation and world-building platform. Users create characters with full psychological profiles and place them in interactive worlds with visual generation.",
      capabilities: "Creative AI systems, visual content generation, interactive storytelling, character behavior modeling",
      whatThisMeans: "We can build sophisticated creative tools and immersive digital experiences",
      technologies: ["AI Image Generation", "Character AI", "Interactive Systems", "Creative Automation"]
    },
    {
      id: 3,
      title: "Business Automation Center",
      icon: <Settings className="h-12 w-12 text-primary" />,
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=500&q=80",
      description: "Multi-business automation dashboard managing 4 separate companies. Real-time webhook processing, cross-business analytics, and intelligent workflow automation.",
      capabilities: "Complex system architecture, real-time data processing, multi-tenant design, webhook integration",
      whatThisMeans: "We can automate and integrate complex business operations at any scale",
      technologies: ["Real-time Processing", "Multi-tenant Architecture", "Workflow Automation", "System Integration"]
    },
    {
      id: 4,
      title: "DoGoodNow",
      icon: <Users className="h-12 w-12 text-primary" />,
      image: "https://images.unsplash.com/photo-1559027615-cd4628902d4a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=500&q=80",
      description: "Social impact platform connecting volunteers with nonprofits. Community features, event management, gamification, and impact tracking systems.",
      capabilities: "Full-stack social platform development, community engagement systems, gamification mechanics",
      whatThisMeans: "We can build complete social platforms and community-driven applications",
      technologies: ["Social Platforms", "Community Systems", "Gamification", "Impact Analytics"]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      {/* Hero Section */}
      <PageSection className="pt-32 pb-20 bg-gray-900 text-white">
        <SectionHeader
          badge="Our Work"
          title="Real Projects That Demonstrate Our Capabilities"
          description="See the actual applications and systems we've built, from AI research platforms to creative tools and business automation systems."
          badgeClassName="text-primary"
          badgeBgClassName="bg-primary/20"
          titleClassName="text-white"
          descriptionClassName="text-white/80"
          centered={true}
        />
      </PageSection>
      
      {/* Projects Grid */}
      <PageSection>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {projects.map((project, index) => (
            <Card 
              key={project.id} 
              className="overflow-hidden border border-border hover:border-primary/50 transition-all duration-300 animate-fade-in-up"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <div className="h-64 overflow-hidden">
                <img 
                  src={project.image} 
                  alt={project.title} 
                  className="w-full h-full object-cover transition-transform hover:scale-105 duration-500" 
                />
              </div>
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  {project.icon}
                  <h3 className="text-2xl font-bold ml-4">{project.title}</h3>
                </div>
                <p className="text-foreground/80 mb-6 leading-relaxed">{project.description}</p>
                <Button variant="outline" className="w-full group mb-4" asChild>
                  <a href={`#project-${project.id}`}>
                    View Full Details
                    <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </a>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </PageSection>
      
      {/* Detailed Project Sections */}
      {projects.map((project) => (
        <PageSection 
          key={project.id} 
          id={`project-${project.id}`} 
          className="border-t border-border scroll-mt-20"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <img 
                src={project.image} 
                alt={project.title} 
                className="w-full h-auto rounded-lg shadow-lg mb-8" 
              />
              <div className="grid grid-cols-1 gap-4">
                <Card className="bg-muted/30">
                  <CardContent className="p-6">
                    <h4 className="font-semibold mb-3 text-lg">Technologies Used</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech, index) => (
                        <span key={index} className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                          {tech}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            <div>
              <div className="flex items-center mb-6">
                {project.icon}
                <h2 className="text-3xl md:text-4xl font-bold ml-4">{project.title}</h2>
              </div>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 text-primary">What We Built</h3>
                <p className="text-foreground/80 leading-relaxed text-lg">{project.description}</p>
              </div>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 text-primary">Capabilities Demonstrated</h3>
                <p className="text-foreground/80 leading-relaxed">{project.capabilities}</p>
              </div>
              
              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 text-primary">What This Means for You</h3>
                <p className="text-foreground/80 leading-relaxed font-medium">{project.whatThisMeans}</p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button className="btn-hover-effect group flex-1">
                  Request Similar Solution
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
                <Button variant="outline" className="flex-1">
                  Learn More
                </Button>
              </div>
            </div>
          </div>
        </PageSection>
      ))}
      
      {/* CTA Section */}
      <PageSection className="bg-gray-900 text-white">
        <div className="text-center">
          <SectionHeader
            badge="Ready to Start?"
            title="Let's Build Something Amazing Together"
            description="These projects represent just a fraction of what's possible. Whether you need behavioral AI, creative tools, business automation, or something entirely new, we have the expertise to bring your vision to life."
            badgeClassName="text-primary"
            badgeBgClassName="bg-primary/20"
            titleClassName="text-white"
            descriptionClassName="text-white/80"
            centered={true}
          />
          <div className="flex flex-col sm:flex-row justify-center gap-4 animate-fade-in">
            <Button size="lg" className="btn-hover-effect">
              Book Discovery Call
            </Button>
            <Button size="lg" variant="outline" className="bg-transparent text-white border-white/20 hover:bg-white/10">
              Discuss Your Project
            </Button>
          </div>
        </div>
      </PageSection>
      
      <Footer />
    </div>
  );
};

export default Portfolio;
